import React, { useState, useContext, useRef } from 'react';
import { Link } from '@ktag/styleguide/dist/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import uniqid from 'uniqid';
import { formatDate } from '@ktag/styleguide/dist/util';
import analytics from '@ktag/styleguide/src/javascripts/analytics/analytics';
import { NewsletterLatest } from 'types/api.types';
import AppContext from 'AppContext';

type EventOptions = {
  mailingId?: number;
  origin: string;
};

const INITIAL_PAGE_SIZE = 5;

const Latest = () => {
  const { t } = useTranslation('newsletter');
  const [showAll, setShowAll] = useState(false);
  const { newsletterApi, latestId, latestCount, viewerBaseUrl, actionsOrigin } =
    useContext(AppContext);
  const latestRef = useRef(null);

  const { data } = useQuery<NewsletterLatest[]>(['latest', latestId, latestCount], () =>
    newsletterApi.getLatestNewsletters(latestId, latestCount),
  );

  const trackEvent = (action: string, options: EventOptions) => {
    analytics.trackEvent('Newsletter', action, options);
  };

  const showAllclickHandler = (e) => {
    e.preventDefault();
    setShowAll(!showAll);
  };

  const sortedItems = data?.sort((a, b) => b.date.localeCompare(a.date));
  const listItemsToRender = showAll
    ? sortedItems
    : sortedItems?.slice(0, INITIAL_PAGE_SIZE);
  const showLoadMoreButton = sortedItems?.length > INITIAL_PAGE_SIZE;

  return (
    <div className="latest" ref={latestRef}>
      <h2 className="latest__header">
        {t('latest_header', { count: listItemsToRender?.length })}
      </h2>
      {listItemsToRender?.length > 0 ? (
        <>
          <ul className="latest__list">
            {listItemsToRender?.map((item) => (
              <li className="latest__list-item" key={uniqid()}>
                <div className="latest__list-item--headline">
                  <a
                    href={`${viewerBaseUrl}#/${item.mailingId}`}
                    className="latest__list-item--title"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() =>
                      trackEvent('MailingViewOpened', {
                        mailingId: item.mailingId,
                        origin: actionsOrigin,
                      })
                    }
                  >
                    {item.title}
                  </a>
                  <span className="latest__list-item--date">{formatDate(item.date)}</span>
                </div>
                {item.description && (
                  <p className="latest__list-item--description">{item.description}</p>
                )}
              </li>
            ))}
          </ul>
          {showLoadMoreButton && !showAll && (
            <Link
              additionalClassNames="latest__showall"
              onClick={showAllclickHandler}
              style={{ textDecoration: 'none' }}
              icon={{
                id: 'chevron',
              }}
              iconPlacement="after"
            >
              {t('latest_show_all')}
            </Link>
          )}
        </>
      ) : (
        <p className="latest__zero-items-text">{t('latest_no_newsletters_available')}</p>
      )}
    </div>
  );
};

export default Latest;
